
import React, { useRef, useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { CarouselButtons } from "./carousel-buttons";

interface ScrollingCarouselProps {
  children: React.ReactNode;
  className?: string;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

export function ScrollingCarousel({ children, className, buttonProps }: ScrollingCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollPrevious(container.scrollLeft > 0);
      setCanScrollNext(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  };

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 100);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    checkScrollButtons();
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    checkScrollButtons();
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent click events from bubbling when dragging
    if (isDragging) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <div className={cn(
      "relative flex items-center",
      canScrollPrevious && "pl-8",
      canScrollNext && "pr-8"
    )}>
      <CarouselButtons
        onPrevious={() => scroll('left')}
        onNext={() => scroll('right')}
        canScrollPrevious={canScrollPrevious}
        canScrollNext={canScrollNext}
        buttonProps={buttonProps}
      />
      <div
        ref={scrollContainerRef}
        className={cn(
          "scrolling-carousel flex-1 flex gap-2 overflow-x-auto cursor-grab active:cursor-grabbing select-none touch-none",
          className
        )}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onScroll={checkScrollButtons}
        onClick={handleClick}
      >
        {children}
      </div>
    </div>
  );
}
